import { Duration, Interval, intervalToDuration } from "date-fns";
import { computed, ComputedRef, Ref } from "vue";

import { Event, EventOrPause } from "@ctrl/controller-rpc";

function toMinutes({ end, start }: Interval): number {
  const endValue = end.valueOf();
  const startValue = start.valueOf();
  if (typeof endValue === "number" && typeof startValue === "number")
    return (endValue - startValue) / 1000 / 60;
  else return 0;
}

export function pixelsOfInterval(
  interval: Interval,
  base: number = 10
): number {
  const minutes = toMinutes(interval);
  if (minutes <= 15) return base;
  else return base + Math.floor(minutes / 15 - 1) * 10;
}

export function minutesOfPixels(pixels: number, base: number = 10): number {
  if (pixels <= base) return 15;
  else return Math.floor((pixels - base) / 10 + 1) * 15;
}

export function durationOfPixels(pixels: number, base: number): Duration {
  return { minutes: minutesOfPixels(pixels, base) };
}

export function useHeightOfDuration(
  interval: Ref<Interval>,
  clock: Ref<Date>,
  base: number = 10
): {
  duration: ComputedRef<Duration>;
  height: ComputedRef<number>;
  minutes: ComputedRef<number>;
  ongoing: ComputedRef<boolean>;
  style: ComputedRef<{ height: string }>;
} {
  const height = computed(() => pixelsOfInterval(interval.value, base));
  const duration = computed(() => {
    return intervalToDuration(interval.value);
  });
  const minutes = computed(() => {
    const { start, end } = interval.value;
    const endValue = end.valueOf();
    const startValue = start.valueOf();
    if (typeof endValue === "number" && typeof startValue === "number")
      return (endValue - startValue) / 1000 / 60;
    else return 0;
  });
  const ongoing = computed(() => {
    const { start, end } = interval.value;
    const now = clock.value;
    return now >= start && now < end;
  });
  const style = computed(() => {
    return {
      height: `${height.value}px`,
    };
  });
  return { duration, height, minutes, ongoing, style };
}

export function useEventProgress(
  event: Ref<EventOrPause> | Ref<Event>,
  clock: Ref<Date>
): {
  progress: ComputedRef<number>;
} {
  const duration = computed(
    () => (event.value.end.valueOf() - event.value.start.valueOf()) / 1000 / 60
  );
  const progressTime = computed(
    () => (clock.value.valueOf() - event.value.start.valueOf()) / 1000 / 60
  );

  const progress = computed(() => (progressTime.value / duration.value) * 100);

  return { progress };
}
